

import {Component, Vue, Watch} from 'vue-property-decorator';
import {LineItemDto, MeDto, PartnerOrderDto, PartnerOrderStatus, listPartnerOrders} from "@/api";
import moment from "moment";
import currency from 'currency.js';
import {applyCharges, eur} from "@/tools/money";
import {DataTableHeader} from "vuetify";
import {formatTime} from '@/tools/time';
import {t} from "@/plugins/translations/t";


@Component
export default class Home extends Vue {

    /** The logged-in user, kept in the store (`GET /api/me`). */
    get user(): MeDto | null {
        return this.$store.state.user;
    }

    private partnerOrders: PartnerOrderDto[] = [];
    private ordersToBePosted: PartnerOrderDto[] = []
    private formatTime = formatTime;

    get orderHeaders(): DataTableHeader[] {
        return [
            {
                text: t("home.orderName"),
                value: "orderName"
            },
            {
                text: t("home.createdAt"),
                value: "orderCreationTime"
            },
            {
                text: t("home.downloadLabel"),
                value: "download"
            }
        ]
    }

    /** Number of the latest `userChanged()`: answers that belong to an older load are dropped. */
    private loadSeq = 0;

    @Watch("user", {deep: true, immediate: true})
    userChanged() {
        // Behind the "account not set up" overlay every partner-scoped request would be a 403. A user without a partner
        // (an admin) gets no data here either: the page renders its orders only for a user with a partner.
        if (!this.user?.partner || !this.$store.getters.hasAccess) {
            return;
        }

        const seq = ++this.loadSeq;

        // An admin with a partner sees only their own orders, like a partner does.
        const partnerId = this.user.partner.id;

        this.ordersLoading = true;
        this.toBePostedLoading = true;

        listPartnerOrders({
            hasLabel: true,
            status: PartnerOrderStatus.EmailSent,
            partnerId
        }).then(orders => {
            if (seq === this.loadSeq) {
                this.ordersToBePosted = orders;
            }
        }).catch(() => {
            // Already shown in the snackbar by the http interceptor.
        }).then(() => {
            if (seq === this.loadSeq) {
                this.toBePostedLoading = false;
            }
        })

        listPartnerOrders({
            from: moment().subtract(1, 'month').toDate(),
            include: "lineItems",
            partnerId
        }).then(orders => {
            if (seq === this.loadSeq) {
                this.partnerOrders = orders;
            }
        }).catch(() => {
            // Already shown in the snackbar by the http interceptor.
        }).then(() => {
            if (seq === this.loadSeq) {
                this.ordersLoading = false;
            }
        })
    }

    downloadLabel(partnerOrder: PartnerOrderDto) {
        window.location.href = "/api/downloadLabel?order=" + encodeURIComponent(partnerOrder.orderName);
    }

    private ordersLoading = false;
    private toBePostedLoading = false;

    get itemsCount() {
        return this.partnerOrders
            .flatMap(o => o.lineItems ?? [])
            .reduce((prev: number, item: LineItemDto) => {
                return prev + item.quantity
            }, 0)
    }

    get orderSum() {
        const orderSum: currency = this.partnerOrders
            .flatMap(o => o.lineItems ?? [])
            .reduce((prev: currency, item: LineItemDto) => {
                const itemCost = eur(item.unitPrice ?? 0)
                    .multiply(item.quantity);

                return prev.add(applyCharges(itemCost, this.user?.partner));
            }, eur(0));

        return orderSum.format()
    }

};
